import React from "react";
import { Alert, Button, Typography } from "antd";
import { useNavigate } from "react-router-dom";
import { Plan } from "../../../Entities/Plan";
import { Subscription } from "../../../Entities/Subscription";
import useAccess from "../../../Hooks/useAccess";
import { formatPlanName } from "../../../Utils/planUtils";
import { useAppContext } from "../../../Context/AppContext/AppContext";
import dayjs from "dayjs";

interface PlanModeSelectorProps {
  currentMode: "base" | "credit" | "free";
  currentPlan: Plan;
  currentSubscription: Subscription;
  hasUpcomingSubscription?: boolean;
  paidSubscriptions?: Subscription[];
}

const PlanModeSelector: React.FC<PlanModeSelectorProps> = ({
  currentMode,
  currentPlan,
  currentSubscription,
  hasUpcomingSubscription = false,
  paidSubscriptions = [],
}) => {
  const navigate = useNavigate();
  const { plans } = useAppContext();
  const {
    canPurchaseCredits,
    canPurchasePlan,
    isPlanBasedAnnual,
  } = useAccess();

  const handleBuyCredits = () => {
    navigate("/credit-purchase");
  };

  const handleChoosePlan = () => {
    navigate("/plan-purchase?action=upgrade");
  };

  const disableAllMode = isPlanBasedAnnual || hasUpcomingSubscription;

  const creditModeEnabled = canPurchaseCredits && !disableAllMode;
  const subscriptionModeEnabled = canPurchasePlan && !disableAllMode;
  const disableCreditMode = !canPurchaseCredits || disableAllMode;
  const disableSubscriptionMode = !canPurchasePlan || disableAllMode;

  const getCreditCTAButtonText = () => {
    if (currentMode === "free" && creditModeEnabled)
      return "Switch to Credit Mode →";
    if (currentMode === "credit" && creditModeEnabled)
      return "Buy More Credits →";
    if (currentMode === "base" && isPlanBasedAnnual && !creditModeEnabled)
      return "Switch to Credit Mode →";
    if (currentMode === "base" && !isPlanBasedAnnual && creditModeEnabled)
      return "Switch to Credit Mode →";
    return "Switch to Credit Mode →";
  };

  const getUpgradeButtonText = () => {
    if (currentMode === "free" && subscriptionModeEnabled)
      return "Upgrade Plan →";
    if (currentMode === "credit" && subscriptionModeEnabled)
      return "Switch to Subscription →";
    if (
      currentMode === "base" &&
      isPlanBasedAnnual &&
      !subscriptionModeEnabled
    )
      return "Upgrade Plan →";
    if (
      currentMode === "base" &&
      !isPlanBasedAnnual &&
      subscriptionModeEnabled
    )
      return "Upgrade Plan →";
    return "Upgrade Plan →";
  };

  const getUpcomingSubscriptionMessage = (
    planId: string,
    startDate: Date,
    mode: string
  ) => {
    const plan = plans.find((p) => p._id === planId);
    if (!plan && mode === "base") {
      return null;
    }
    const planName = formatPlanName(
      plan ? plan?.name : "QualifyMe Credit Plan"
    );
    const formattedStartDate = dayjs(startDate).format("DD MMM YYYY");
    return `You have a paid subscription for ${planName} (${mode}-based) from ${formattedStartDate}. Plan changes will be available after it starts.`;
  };

  const getWarningMessage = (): string | null => {
    if (hasUpcomingSubscription && paidSubscriptions[0]) {
      const paid = paidSubscriptions[0];
      const message = getUpcomingSubscriptionMessage(
        paid.planId,
        paid.startDate,
        paid.mode
      );
      return message ?? "You have an upcoming subscription. Plan changes will be available after it starts.";
    }
    if (isPlanBasedAnnual) {
      const planName = formatPlanName(currentPlan.name);
      return `You currently have an active annual subscription (${planName}). Plan changes will be available after your subscription ends. In the meantime, you can purchase Job Post and Application add-ons.`;
    }
    return null;
  };

  const warningMessage = getWarningMessage();

  return (
    <div className="feature">
      <div className="feature__container">
        <div className="feature__header">
          <Typography.Title level={4} style={{ margin: 0 }}>
            Flexible Subscription Plans & Application Credits
          </Typography.Title>

          {disableAllMode && warningMessage && (
            <Alert message={warningMessage} type="warning" />
          )}
        </div>

        <div className="feature__grid">
          {/* Small Cards */}
          <div className="card card-small">
            <Typography.Title level={5} style={{ margin: 0 }}>
              Application Credits
            </Typography.Title>
            <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
              Pay only for the applications you submit. Prepay for credits
              anytime and enjoy full access to all features — just like our
              subscription plans.
            </Typography.Paragraph>
            <Button
              type="primary"
              block
              onClick={handleBuyCredits}
              disabled={disableCreditMode}
            >
              {getCreditCTAButtonText()}
            </Button>
          </div>
          {/* Big Card */}
          <div className="card card-large">
            <div className="content">
              <Typography.Title level={5} style={{ margin: 0 }}>
                Subscription Plans
              </Typography.Title>
              <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
                Get full platform access with monthly or yearly billing. Perfect
                for teams that hire regularly and need consistent features and
                limits.
              </Typography.Paragraph>

              <Button
                type="primary"
                onClick={handleChoosePlan}
                disabled={disableSubscriptionMode}
              >
                {getUpgradeButtonText()}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanModeSelector;
